import { cx } from '../ui';
import { useCopy } from '../strings/useCopy';
import type { GridPreviewBinding } from './useGridPreview';
import styles from './listing.module.css';

/**
 * Everything a row draws *inside* a thumbnail frame that is not the picture, as one node — the
 * hover preview's flipbook tile, and the corner note under it. It exists because five lists draw it
 * (the feed card, the history card, the playlist entry, and the watch page's rail and playlist panel)
 * and a copy per screen is five chances for one of them to drift; `Thumbnail.overlay` takes a node it
 * knows nothing about, so `ui/` stays domain-free.
 *
 * **The frame's bottom-left corner holds one note at a time**, which is why both live here: the
 * preview's own ("Preview…", "No preview") while a hover is running, and the "Watched" mark
 * otherwise (2026-08-06). The preview's wins while it is showing — it is about something happening
 * right now, and the watched mark is still there a second later when the note goes.
 *
 * `aria-hidden` throughout, like everything on a thumbnail: the frame is already hidden from
 * assistive technology so that each card has exactly one link, and none of this is a fact the row
 * does not carry elsewhere — the tile is the picture moving, the preview note is about a fetch, and
 * the watched mark is the row's own marker drawn again (`listing/model.ts`).
 */
export function ThumbnailOverlay({
  preview = null,
  watched = false,
}: {
  /**
   * The hover preview's binding, or nothing at all. The watch page's two side-column lists pass
   * nothing: neither of them runs the flipbook, and the mark alone is still worth drawing there.
   */
  readonly preview?: GridPreviewBinding | null;
  /**
   * Draw the "Watched" mark. The History grid passes nothing: every row there is watched by
   * construction, so the mark would sit on all of them and say nothing about any.
   */
  readonly watched?: boolean;
}) {
  const words = useCopy().text.listing;
  const tile = preview?.tile ?? null;
  const status = preview?.status ?? null;

  return (
    <>
      {tile === null ? null : (
        <span className={styles.previewTileBox} aria-hidden="true">
          {/* Natural-size crop, scaled from its own corner — so the centring is the inline
              position, not the transform (a percentage translate would measure the unscaled
              box and centre the wrong rectangle). */}
          <span
            className={styles.previewTile}
            style={{
              left: tile.leftPx,
              top: tile.topPx,
              width: tile.crop.tileWidth,
              height: tile.crop.tileHeight,
              backgroundImage: `url(${tile.crop.imageUrl})`,
              backgroundPosition: `-${tile.crop.offsetX}px -${tile.crop.offsetY}px`,
              transform: `scale(${tile.crop.scale})`,
            }}
          />
        </span>
      )}

      {status !== null ? (
        <span className={styles.previewNote} aria-hidden="true">
          <span
            className={cx(styles.previewNoteDot, status === 'failed' && styles.previewNoteDotFailed)}
          />
          {status === 'failed' ? words.preview.failed : words.preview.loading}
        </span>
      ) : watched ? (
        <span className={styles.previewNote} aria-hidden="true">
          <WatchedGlyph />
          {words.progress.watched}
        </span>
      ) : null}
    </>
  );
}

/** A check where the preview note draws its pulsing dot — the same slot, at the same size. */
function WatchedGlyph() {
  return (
    <svg className={styles.watchedGlyph} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m5 12.8 4.6 4.6L19 7.4"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
